import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import invitationService, { InvitationPreview } from '../services/invitationService'
import { rememberInviteToken } from '../utils/inviteRedirect'
import { useTranslation } from '../hooks/use-i18n'

const InviteLanding: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')?.trim() || ''
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth)
  const [preview, setPreview] = useState<InvitationPreview | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!token) {
      setError(t('inviteLanding.missing'))
      setLoading(false)
      return
    }
    rememberInviteToken(token)
    invitationService
      .getPreview(token)
      .then((data) => {
        setPreview(data)
        setError('')
      })
      .catch(() => {
        setError(t('inviteLanding.invalid'))
      })
      .finally(() => setLoading(false))
  }, [token, t])

  useEffect(() => {
    if (!preview || preview.expired || loading) return
    const previewEmail = String(preview.recipientEmail || '').toLowerCase()
    const userEmail = String(user?.email || '').toLowerCase()
    if (isAuthenticated && userEmail && previewEmail && userEmail === previewEmail) {
      navigate('/invitations', { replace: true })
    }
  }, [isAuthenticated, user, preview, loading, navigate])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  const wrongAccount =
    isAuthenticated &&
    preview &&
    String(user?.email || '').toLowerCase() !== String(preview.recipientEmail || '').toLowerCase()

  return (
    <div className="min-h-screen gradient-bg flex items-center justify-center px-4 py-16">
      <div className="max-w-md w-full glass-strong rounded-2xl p-6 sm:p-8 text-center">
        <h1 className="font-display text-2xl font-bold text-foreground mb-3">{t('inviteLanding.title')}</h1>
        {error || preview?.expired ? (
          <p className="text-destructive">{error || t('inviteLanding.expired')}</p>
        ) : wrongAccount ? (
          <p className="text-foreground/90">
            {t('inviteLanding.wrongAccount', { email: preview?.recipientEmail || '' })}
          </p>
        ) : (
          <>
            <p className="text-foreground/90 mb-6">
              {t('inviteLanding.body', {
                sender: preview?.senderName || t('inviteLanding.someone'),
                target: preview?.roomName || t('invitations.aRoom'),
              })}
            </p>
            <div className="flex flex-col gap-3">
              <Link
                to={`/signup?invite=${encodeURIComponent(token)}&email=${encodeURIComponent(preview?.recipientEmail || '')}`}
                className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold"
              >
                {t('inviteLanding.signUp')}
              </Link>
              <Link
                to={`/login?invite=${encodeURIComponent(token)}&email=${encodeURIComponent(preview?.recipientEmail || '')}`}
                className="w-full py-3 rounded-xl border border-border text-foreground font-semibold"
              >
                {t('inviteLanding.logIn')}
              </Link>
            </div>
            <p className="text-xs text-muted-foreground mt-4">{t('inviteLanding.noAutoJoin')}</p>
          </>
        )}
      </div>
    </div>
  )
}

export default InviteLanding
